import { Link } from "@tanstack/react-router";
import { ShoppingCart, Menu, X, User, LogOut, Package } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useCart } from "@/hooks/use-cart";
import { useAuth } from "@/hooks/use-auth";

const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { totalItems } = useCart();
  const { user, signOut } = useAuth();
  const menuRef = useRef<HTMLDivElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const closeMenu = useCallback(() => {
    setMobileOpen(false);
    toggleRef.current?.focus();
  }, []);

  // ESC key closes menu
  useEffect(() => {
    if (!mobileOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMenu();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [mobileOpen, closeMenu]);

  // Focus trap inside mobile menu
  useEffect(() => {
    if (!mobileOpen || !menuRef.current) return;

    const menu = menuRef.current;
    const focusables = () => Array.from(menu.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    // Focus first item on open
    const items = focusables();
    if (items.length > 0) items[0].focus();

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const els = focusables();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleTab);
    return () => document.removeEventListener("keydown", handleTab);
  }, [mobileOpen]);

  // Click outside closes menu
  useEffect(() => {
    if (!mobileOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target as Node) &&
        toggleRef.current &&
        !toggleRef.current.contains(e.target as Node)
      ) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [mobileOpen, closeMenu]);

  return (
    <header className="sticky top-0 z-50 bg-surface-elevated/90 backdrop-blur-xl border-b border-border">
      <div className="h-1 bg-gucci-stripes" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-full bg-primary flex items-center justify-center border border-electric/40">
              <span className="text-primary-foreground font-display font-bold text-base italic">A</span>
            </div>
            <span className="font-display font-bold text-2xl text-foreground tracking-tight uppercase">
              Angle
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-1 min-h-[24px] inline-flex items-center" activeProps={{ className: "text-sm font-medium text-foreground py-1 min-h-[24px] inline-flex items-center" }}>
              Accueil
            </Link>
            <Link to="/catalog" className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-1 min-h-[24px] inline-flex items-center" activeProps={{ className: "text-sm font-medium text-foreground py-1 min-h-[24px] inline-flex items-center" }}>
              Collection
            </Link>
          </nav>

          <div className="flex items-center gap-3">
            {user ? (
              <div className="flex items-center gap-2">
                <Link
                  to="/orders"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                >
                  <Package className="w-4 h-4" />
                  Mes commandes
                </Link>
                <button
                  onClick={signOut}
                  className="p-2 rounded-full hover:bg-accent transition-colors"
                  aria-label="Se déconnecter"
                  title="Se déconnecter"
                >
                  <LogOut className="w-4 h-4 text-muted-foreground" />
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-foreground border border-border hover:bg-accent transition-colors"
              >
                <User className="w-4 h-4" />
                Connexion
              </Link>
            )}
            <Link to="/cart" aria-label="Panier" className="relative p-2 rounded-full hover:bg-accent transition-colors">
              <ShoppingCart className="w-5 h-5 text-foreground" />
              {totalItems > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full bg-gradient-electric text-electric-foreground text-xs font-bold flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </Link>
            <button
              ref={toggleRef}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav"
              className="md:hidden p-2 rounded-full hover:bg-accent transition-colors"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div
          ref={menuRef}
          id="mobile-nav"
          role="dialog"
          aria-modal="true"
          aria-label="Menu de navigation"
          className="md:hidden border-t border-border bg-surface-elevated"
        >
          <nav className="flex flex-col p-4 gap-2">
            <Link to="/" onClick={() => setMobileOpen(false)} className="px-4 py-3 rounded-lg text-sm font-medium hover:bg-accent transition-colors">
              Accueil
            </Link>
            <Link to="/catalog" onClick={() => setMobileOpen(false)} className="px-4 py-3 rounded-lg text-sm font-medium hover:bg-accent transition-colors">
              Collection
            </Link>
            {!user && (
              <Link to="/login" onClick={() => setMobileOpen(false)} className="px-4 py-3 rounded-lg text-sm font-medium hover:bg-accent transition-colors flex items-center gap-2">
                <User className="w-4 h-4" /> Connexion
              </Link>
            )}
            {user && (
              <>
                <Link to="/orders" onClick={() => setMobileOpen(false)} className="px-4 py-3 rounded-lg text-sm font-medium hover:bg-accent transition-colors flex items-center gap-2">
                  <Package className="w-4 h-4" /> Mes commandes
                </Link>
                <button onClick={() => { signOut(); setMobileOpen(false); }} className="px-4 py-3 rounded-lg text-sm font-medium hover:bg-accent transition-colors text-left flex items-center gap-2">
                  <LogOut className="w-4 h-4" /> Se déconnecter
                </button>
              </>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
